import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/UserOrders.css";
import { FaBoxOpen } from "react-icons/fa";

/**
 * Admin AllOrders - grouped view similar to MyOrder
 * Expects backend response: { orders: [ { orderId, productImage, productName,
 *   productDescription, quantity, size, totalPrice, orderDate, deliveryDate,
 *   deliveryStatus, deliveryPersonName, deliveryPersonContact, userName,
 *   userPhone, deliveryAddress: { addressLine, city, downtown } }, ... ] }
 */

const AllOrders = () => {
  const [rawOrders, setRawOrders] = useState([]); // flat list from backend
  const [groupedOrders, setGroupedOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // search state
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const getAllOrder = async () => {
      try {
        const resp = await axios.get(
          "https://goldenhides.in/api/user/admin/allorder"
        );
        const data = resp.data || {};
        const orders = data.orders ?? data;
        setRawOrders(Array.isArray(orders) ? orders : []);
      } catch (err) {
        console.error("Failed to load admin orders", err);
        setRawOrders([]);
      } finally {
        setLoading(false);
      }
    };

    getAllOrder();
  }, []);

  // group rawOrders by orderId
  useEffect(() => {
    if (!rawOrders || rawOrders.length === 0) {
      setGroupedOrders([]);
      return;
    }

    const map = new Map();
    rawOrders.forEach((o) => {
      const id = o.orderId ?? `order_${Math.random().toString(36).slice(2, 9)}`;
      if (!map.has(id)) {
        map.set(id, {
          orderId: id,
          orderDate: o.orderDate,
          deliveryDate: o.deliveryDate,
          deliveryStatus: o.deliveryStatus,
          deliveryPersonName: o.deliveryPersonName,
          deliveryPersonContact: o.deliveryPersonContact,
          userName: o.userName,
          userPhone: o.userPhone,
          deliveryAddress: o.deliveryAddress ?? {},
          items: [],
        });
      }

      map.get(id).items.push({
        productId: o.productId,
        productName: o.productName,
        productDescription: o.productDescription,
        productImage: o.productImage,
        quantity: o.quantity,
        size: o.size,
        totalPrice: o.totalPrice,
      });
    });

    setGroupedOrders(Array.from(map.values()));
  }, [rawOrders]);

  const formatDateFromEpoch = (epochTime) => {
    if (!epochTime) return "";
    const d = new Date(Number(epochTime));
    const opts = { day: "2-digit", month: "short", year: "numeric" };
    const datePart = d.toLocaleDateString(undefined, opts);
    const timePart = d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${datePart}, ${timePart}`;
  };

  // filtered list based on searchTerm (partial, case-insensitive)
  const filteredOrders = groupedOrders.filter((o) =>
    o.orderId.toString().toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  const handleClearSearch = () => setSearchTerm("");

  if (loading) {
    return (
      <div className="mt-3 px-2">
        <div className="text-center mb-4">
          <h2 className="fw-bold text-success">
            <FaBoxOpen className="me-2" />
            All Orders
          </h2>
        </div>
        <div className="card form-card ms-2 me-2 mb-5 custom-bg">
          <div className="card-body text-center">Loading orders...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 px-2">
      <div className="container-fluid">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-column flex-md-row">
          <div className="text-start">
            <h2 className="fw-bold text-success mb-0">
              <FaBoxOpen className="me-2" />
              All Orders
            </h2>
            <p className="text-muted mb-0">
              All customer orders — grouped by order id
            </p>
          </div>

          {/* Search box */}
          <div className="mt-3 mt-md-0 d-flex align-items-center gap-2">
            <input
              type="search"
              className="form-control"
              placeholder="Search by Order ID (partial match)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") handleClearSearch();
              }}
              style={{ minWidth: 260 }}
            />
            {searchTerm ? (
              <button
                className="btn btn-outline-secondary"
                onClick={handleClearSearch}
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>

        {filteredOrders.length === 0 ? (
          <div className="card form-card ms-2 me-2 mb-5 custom-bg">
            <div className="card-body text-center">
              <p className="mb-0">
                {groupedOrders.length === 0
                  ? "No orders found."
                  : `No orders match "${searchTerm}".`}
              </p>
            </div>
          </div>
        ) : (
          filteredOrders.map((order) => {
            const orderTotal = order.items.reduce(
              (acc, it) => acc + Number(it.totalPrice || 0),
              0
            );

            const showDeliveryInfo =
              order.deliveryPersonName &&
              order.deliveryPersonContact &&
              order.deliveryPersonName !== "Pending" &&
              order.deliveryPersonContact !== "Pending";

            return (
              <div
                key={order.orderId}
                className="card form-card ms-2 me-2 mb-4 custom-bg orders-group-card"
              >
                {/* Order header */}
                <div className="card-header d-flex align-items-center justify-content-between bg-white">
                  <div className="d-flex align-items-center gap-4">
                    <div>
                      <div className="small text-muted">Order ID</div>
                      <div style={{ fontWeight: 700 }}>#{order.orderId}</div>
                    </div>

                    <div>
                      <div className="small text-muted">Items</div>
                      <div style={{ fontWeight: 600 }}>
                        {order.items.length} item
                        {order.items.length > 1 ? "s" : ""}
                      </div>
                    </div>

                    <div>
                      <div className="small text-muted">Ordered</div>
                      <div style={{ fontWeight: 600 }}>
                        {formatDateFromEpoch(order.orderDate)}
                      </div>
                    </div>

                    <div>
                      <div className="small text-muted">Customer</div>
                      <div style={{ fontWeight: 600 }}>
                        {order.userName || "-"}
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <div className="text-end small">
                      <div className="small text-muted">Contact</div>
                      <div style={{ fontWeight: 600 }}>
                        {order.userPhone || "-"}
                      </div>
                    </div>

                    {showDeliveryInfo && (
                      <div className="text-end small">
                        <div className="small text-muted">Delivery Person</div>
                        <div style={{ fontWeight: 600 }}>
                          {order.deliveryPersonName}
                        </div>
                        <div className="small">
                          {order.deliveryPersonContact}
                        </div>
                      </div>
                    )}

                    <div>
                      <span
                        className={`badge order-status-badge ${
                          (order.deliveryStatus || "").toLowerCase() ===
                          "pending"
                            ? "badge-pending"
                            : "badge-other"
                        }`}
                      >
                        {order.deliveryStatus || "Pending"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Body: product cards */}
                <div className="card-body">
                  <div className="row g-3">
                    {order.items.map((item, idx) => (
                      <div
                        key={`${order.orderId}_item_${idx}`}
                        className="col-12 col-md-4"
                      >
                        <div className="order-product-card p-3 h-100 shadow-sm rounded">
                          <div className="d-flex gap-3">
                            <div style={{ width: 90, height: 90 }}>
                              {item.productImage ? (
                                <img
                                  src={`https://goldenhides.in/api/product/${item.productImage}`}
                                  alt={item.productName}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    borderRadius: 6,
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    background: "#f1f1f1",
                                    borderRadius: 6,
                                  }}
                                />
                              )}
                            </div>

                            <div className="flex-grow-1">
                              <div style={{ fontWeight: 700 }}>
                                {item.productName}
                              </div>
                              <div className="small text-muted mt-1">
                                {item.productDescription
                                  ? item.productDescription.slice(0, 80) +
                                    (item.productDescription.length > 80
                                      ? "..."
                                      : "")
                                  : ""}
                              </div>

                              <div className="mt-2 small text-muted">
                                Qty: {item.quantity} &nbsp;|&nbsp; Size:{" "}
                                {item.size || "-"}
                              </div>
                            </div>

                            <div style={{ textAlign: "right", minWidth: 90 }}>
                              <div
                                className="text-success"
                                style={{ fontWeight: 700 }}
                              >
                                ₹ {Number(item.totalPrice || 0).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer: order total & address */}
                <div className="card-footer bg-white d-flex justify-content-between align-items-center">
                  <div className="small text-muted">
                    <div>Delivery Address:</div>
                    <div style={{ fontWeight: 600 }}>
                      {order.deliveryAddress?.addressLine ?? "-"},{" "}
                      {order.deliveryAddress?.city ?? "-"}{" "}
                      {order.deliveryAddress?.downtown
                        ? `, ${order.deliveryAddress.downtown}`
                        : ""}
                    </div>
                  </div>

                  <div
                    style={{ fontWeight: 700, color: "#0a7a3b", fontSize: 18 }}
                  >
                    Total ₹ {orderTotal.toFixed(2)}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AllOrders;
